import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import type { CreateAssignmentInput } from "@/types/assignments";

interface CreateAssignmentResult {
  successCount: number;
  errorCount: number;
  skippedCount: number;
  skippedUsers: { userId: string; reason: string }[];
  createdIds: string[];
}

interface CreatedAssignment {
  userId: string;
  assignmentId: string;
  itemId: string;
  itemName: string;
  itemType: "curriculum" | "course";
  dueDate?: string;
  required: boolean;
  notes?: string;
}

async function sendAssignmentNotifications(
  assignments: CreatedAssignment[],
  assignedByName: string
) {
  // Send notifications in parallel (fire and forget, don't block)
  const notificationPromises = assignments.map(async (assignment) => {
    try {
      const { error } = await supabase.functions.invoke("notify-learning-assignment", {
        body: {
          user_id: assignment.userId,
          item_type: assignment.itemType,
          item_id: assignment.itemId,
          item_name: assignment.itemName,
          due_date: assignment.dueDate,
          required: assignment.required,
          notes: assignment.notes,
          assigned_by_name: assignedByName,
        },
      });

      if (error) {
        console.error(`Failed to send notification for assignment ${assignment.assignmentId}:`, error);
      } else {
        console.log(`Notification sent for assignment ${assignment.assignmentId}`);
      }
    } catch (err) {
      console.error(`Error sending notification for assignment ${assignment.assignmentId}:`, err);
    }
  });

  // Wait for all notifications but don't throw if some fail
  await Promise.allSettled(notificationPromises);
}

export function useCreateAssignment() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAssignmentInput): Promise<CreateAssignmentResult> => {
      if (!user?.id) {
        throw new Error("You must be logged in to create assignments");
      }

      const result: CreateAssignmentResult = {
        successCount: 0,
        errorCount: 0,
        skippedCount: 0,
        skippedUsers: [],
        createdIds: [],
      };

      const { type, item_id, user_ids, due_date, required, notes } = input;

      // Fetch item name for notifications
      let itemName = "Learning Content";
      if (type === "curriculum") {
        const { data: curriculum } = await supabase
          .from("curricula")
          .select("title")
          .eq("id", item_id)
          .single();
        itemName = curriculum?.title || "Curriculum";
      } else {
        const { data: course } = await supabase
          .from("courses")
          .select("title")
          .eq("id", item_id)
          .single();
        itemName = course?.title || "Course";
      }

      // Fetch assigner's name for notifications
      const { data: assignerProfile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();
      const assignedByName = assignerProfile?.full_name || "An administrator";

      // Track created assignments for notifications
      const createdAssignments: CreatedAssignment[] = [];

      // Process each user
      for (const userId of user_ids) {
        try {
          if (type === "curriculum") {
            // Check for existing curriculum assignment
            const { data: existing } = await supabase
              .from("curriculum_assignments")
              .select("id")
              .eq("user_id", userId)
              .eq("curriculum_id", item_id)
              .maybeSingle();

            if (existing) {
              result.skippedCount++;
              result.skippedUsers.push({
                userId,
                reason: "Already assigned this curriculum",
              });
              continue;
            }

            // Create curriculum assignment
            const { data, error } = await supabase
              .from("curriculum_assignments")
              .insert({
                user_id: userId,
                curriculum_id: item_id,
                assigned_by: user.id,
                assigned_at: new Date().toISOString(),
                due_date: due_date || null,
                required: required,
                notes: notes || null,
              })
              .select("id")
              .single();

            if (error) {
              console.error("Error creating curriculum assignment:", error);
              result.errorCount++;
              result.skippedUsers.push({
                userId,
                reason: error.message,
              });
            } else {
              result.successCount++;
              result.createdIds.push(data.id);
              createdAssignments.push({
                userId,
                assignmentId: data.id,
                itemId: item_id,
                itemName,
                itemType: "curriculum",
                dueDate: due_date,
                required,
                notes,
              });
            }
          } else {
            // Check for existing course assignment
            const { data: existing } = await supabase
              .from("course_assignments")
              .select("id")
              .eq("user_id", userId)
              .eq("course_id", item_id)
              .maybeSingle();

            if (existing) {
              result.skippedCount++;
              result.skippedUsers.push({
                userId,
                reason: "Already assigned this course",
              });
              continue;
            }

            // Create course assignment
            const { data, error } = await supabase
              .from("course_assignments")
              .insert({
                user_id: userId,
                course_id: item_id,
                assigned_by: user.id,
                assigned_at: new Date().toISOString(),
                due_date: due_date || null,
                required: required,
                notes: notes || null,
              })
              .select("id")
              .single();

            if (error) {
              console.error("Error creating course assignment:", error);
              result.errorCount++;
              result.skippedUsers.push({
                userId,
                reason: error.message,
              });
            } else {
              result.successCount++;
              result.createdIds.push(data.id);
              createdAssignments.push({
                userId,
                assignmentId: data.id,
                itemId: item_id,
                itemName,
                itemType: "course",
                dueDate: due_date,
                required,
                notes,
              });
            }
          }
        } catch (err) {
          console.error("Unexpected error creating assignment:", err);
          result.errorCount++;
          result.skippedUsers.push({
            userId,
            reason: err instanceof Error ? err.message : "Unknown error",
          });
        }
      }

      // Send email notifications for all successfully created assignments
      if (createdAssignments.length > 0) {
        // Fire and forget - don't block the UI
        sendAssignmentNotifications(createdAssignments, assignedByName).catch((err) => {
          console.error("Error sending assignment notifications:", err);
        });
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate assignment queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["all-learning-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["user-learning-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["user_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["all_learning_assignments"] });
    },
  });
}
